import * as React from "react";
import { getFeatureToggles } from "@teamprevent/common/getFeatureToggles";
import * as FeatureToggleNames from "@teamprevent/common/featureToggleNames";

import { useSelector, useDispatch } from "react-redux";
import { Accordion, Container, Grid, Icon, Header, Message, AccordionTitleProps } from "semantic-ui-react";

import { caseMessagesSelector } from "@teamprevent/common/messaging/selectors";
import { MessageDetails } from "@teamprevent/common/messaging/models";
import { getAllCaseMessages } from "@teamprevent/common/messaging/actions";
import { CaseSubjectMessage } from "@teamprevent/common/messaging/components/CaseSubjectMessages";
import { MessagingReplyModalButton } from "@teamprevent/common/messaging/components/MessagingReplyModalButton";
import { ThunkDispatch } from "redux-thunk";

import { CaseConversationDocumentText } from "./CaseConversationDocumentText";

export interface CaseConversationProps {
    caseId: string;
    subject: string;
    includeAllSubjects: boolean;
    showReply: boolean;
    customerId: string;
    previewConversation?: boolean;
    hideTitle?: boolean;
    employeeName: string;
}

const CaseConversation: React.FC<CaseConversationProps> = ({
    caseId, subject, includeAllSubjects, showReply, customerId, previewConversation, hideTitle, employeeName
}) => {
    const enableCaseConversationImprovements = getFeatureToggles()[FeatureToggleNames.CaseConversationImprovements];
    const dispatch = useDispatch<ThunkDispatch<any, any, any>>();

    const onlyUseConversationMessages = (messageDetails: MessageDetails[]): MessageDetails[] => {
        if (includeAllSubjects) {
            return messageDetails;
        }

        return messageDetails.filter(m => m.subject === subject);
    };

    let messageDetails: MessageDetails[] = useSelector(caseMessagesSelector);
    messageDetails = onlyUseConversationMessages(messageDetails);

    const orderedMessages = messageDetails.sort((a, b) => b.subjectStartDate.valueOf() - a.subjectStartDate.valueOf());

    const [activeIndex, SetActiveIndex] = React.useState<number | string | undefined>(-1);

    const onSubjectClick = React.useCallback((event: React.MouseEvent<HTMLDivElement>, data: AccordionTitleProps) => {
        const { index } = data;
        const newIndex = activeIndex === index ? -1 : index;

        SetActiveIndex(newIndex);
    }, [activeIndex]);

    React.useEffect(() => {
        if (enableCaseConversationImprovements === false) {
            dispatch(getAllCaseMessages(caseId));
        }
    }, [caseId, subject, includeAllSubjects, dispatch, enableCaseConversationImprovements]);

    return (
        <Container className={"conversation-margin"}>
            <Grid>
                {orderedMessages.map((message, index) => {
                    return (
                        <Grid.Row key={index}>
                            <Grid.Column width={16}>
                                <Message>
                                    <Accordion>
                                        {!hideTitle ?
                                            <Accordion.Title
                                                active={activeIndex === index || previewConversation}
                                                index={index}
                                                onClick={onSubjectClick}
                                            >
                                                <Header as='h3'><Icon name="dropdown" />{message.subject}</Header>
                                            </Accordion.Title>
                                            :
                                            <h3>Conversation history:</h3>
                                        }
                                        <Accordion.Content active={activeIndex === index || previewConversation}>
                                            {showReply &&
                                                <>
                                                    <MessagingReplyModalButton
                                                        caseId={caseId}
                                                        subject={message.subject}
                                                        buttonText={"Conversation Actions"}
                                                        showReply={showReply}
                                                        customerId={customerId}
                                                        employeeName={employeeName}
                                                    />
                                                    <CaseConversationDocumentText className="case-conversation-note" />
                                                </>}
                                            <CaseSubjectMessage messages={message.messages} caseId={caseId} />
                                        </Accordion.Content>
                                    </Accordion>
                                </Message>
                            </Grid.Column>
                        </Grid.Row>
                    );
                })}
            </Grid>
        </Container>
    );
};

export { CaseConversation };
